import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  BookOpen, ArrowLeft, Save, RotateCcw, Settings, 
  Users, Trophy, Clock, Phone, MessageSquare, Calendar, Info
} from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';

const API = process.env.REACT_APP_BACKEND_URL;

const AIAgentPlaybook = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [originalConfig, setOriginalConfig] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  useEffect(() => {
    if (config && originalConfig) {
      setHasChanges(JSON.stringify(config) !== JSON.stringify(originalConfig));
    }
  }, [config, originalConfig]);

  const fetchConfig = async () => {
    try {
      const token = sessionStorage.getItem('auth_token');
      const response = await axios.get(`${API}/api/ai-intelligence/modules/agent_playbook`, {
        params: { token }
      });
      
      if (response.data.success) {
        setConfig(response.data.module);
        setOriginalConfig(JSON.parse(JSON.stringify(response.data.module)));
      }
    } catch (error) {
      console.error('Error fetching config:', error);
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = sessionStorage.getItem('auth_token');
      const response = await axios.put(
        `${API}/api/ai-intelligence/modules/agent_playbook?token=${token}`,
        {
          enabled: config.enabled,
          settings: config.settings
        }
      );
      
      if (response.data.success) {
        setOriginalConfig(JSON.parse(JSON.stringify(config)));
        toast.success('Configuration saved successfully');
      }
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      const token = sessionStorage.getItem('auth_token');
      await axios.post(`${API}/api/ai-intelligence/modules/agent_playbook/reset?token=${token}`);
      toast.success('Reset to defaults');
      fetchConfig();
    } catch (error) {
      toast.error('Failed to reset');
    }
  };

  const updateSetting = (key, value) => {
    setConfig(prev => ({
      ...prev,
      settings: { ...prev.settings, [key]: value }
    }));
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="agent-playbook-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/ai-intelligence')}
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-green-500 to-green-600 rounded-xl">
              <BookOpen className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Agent Playbook AI</h1>
              <p className="text-sm text-slate-500">Learn from top performers</p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {hasChanges && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
              Unsaved Changes
            </Badge>
          )}
          <Switch
            checked={config?.enabled}
            onCheckedChange={(checked) => setConfig(prev => ({ ...prev, enabled: checked }))}
            data-testid="module-toggle"
          />
          <span className={`text-sm font-medium ${config?.enabled ? 'text-green-600' : 'text-slate-500'}`}>
            {config?.enabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>
      </div>

      {!config?.enabled && (
        <Alert className="bg-amber-50 border-amber-200">
          <Info className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            This module is currently disabled. Enable it to generate AI playbooks.
          </AlertDescription>
        </Alert>
      )}

      {/* Learning Source */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Trophy className="h-5 w-5 text-green-600" />
            Learning Configuration
          </CardTitle>
          <CardDescription>
            Configure how the AI learns from top performers
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label className="mb-2 block">Learn from Top N Agents</Label>
              <div className="flex items-center gap-4">
                <Input
                  type="number"
                  value={config?.settings?.learn_from_top_agents || 3}
                  onChange={(e) => updateSetting('learn_from_top_agents', parseInt(e.target.value))}
                  min={1}
                  max={10}
                  className="w-24"
                  data-testid="top-agents"
                />
                <span className="text-sm text-slate-500">top performing agents</span>
              </div>
            </div>

            <div>
              <Label className="mb-2 block">Minimum Successful Leads</Label>
              <div className="flex items-center gap-4">
                <Input
                  type="number"
                  value={config?.settings?.min_successful_leads || 10}
                  onChange={(e) => updateSetting('min_successful_leads', parseInt(e.target.value))}
                  min={5}
                  max={50}
                  className="w-24"
                  data-testid="min-leads"
                />
                <span className="text-sm text-slate-500">conversions to qualify</span>
              </div>
            </div>
          </div>

          <div>
            <Label className="mb-2 block">Refresh Frequency</Label>
            <div className="flex items-center gap-4">
              <Input
                type="number"
                value={config?.settings?.refresh_frequency_days || 7}
                onChange={(e) => updateSetting('refresh_frequency_days', parseInt(e.target.value))}
                min={1}
                max={30}
                className="w-24"
                data-testid="refresh-days"
              />
              <span className="text-sm text-slate-500">days between playbook updates</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Apply Mode */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings className="h-5 w-5 text-green-600" />
            Application Mode
          </CardTitle>
          <CardDescription>
            How playbook recommendations are shown to agents
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                config?.settings?.apply_mode === 'show_only'
                  ? 'border-green-500 bg-green-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              onClick={() => updateSetting('apply_mode', 'show_only')}
              data-testid="mode-show-only"
            >
              <span className="font-medium">Show Only</span>
              <p className="text-sm text-slate-500 mt-1">
                Display playbook as guidance without prompts
              </p>
            </div>
            
            <div
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                config?.settings?.apply_mode === 'show_and_recommend'
                  ? 'border-green-500 bg-green-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              onClick={() => updateSetting('apply_mode', 'show_and_recommend')}
              data-testid="mode-recommend"
            >
              <span className="font-medium">Show & Recommend</span>
              <p className="text-sm text-slate-500 mt-1">
                Show playbook and prompt specific actions
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-4">
            <div
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                config?.settings?.playbook_scope === 'team_wide'
                  ? 'border-green-500 bg-green-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              onClick={() => updateSetting('playbook_scope', 'team_wide')}
              data-testid="scope-team"
            >
              <div className="flex items-center gap-2 mb-1">
                <Users className="h-4 w-4 text-green-600" />
                <span className="font-medium">Team-Wide</span>
              </div>
              <p className="text-sm text-slate-500">
                Same playbook for all agents
              </p>
            </div>
            
            <div
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                config?.settings?.playbook_scope === 'agent_specific'
                  ? 'border-green-500 bg-green-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              onClick={() => updateSetting('playbook_scope', 'agent_specific')}
              data-testid="scope-agent"
            >
              <div className="flex items-center gap-2 mb-1">
                <Users className="h-4 w-4 text-green-600" />
                <span className="font-medium">Agent-Specific</span>
              </div>
              <p className="text-sm text-slate-500">
                Personalized playbooks per agent
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pattern Inclusion */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-green-600" />
            Pattern Analysis
          </CardTitle>
          <CardDescription>
            What patterns to include in playbook generation
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-green-600" />
              <div>
                <Label className="font-medium">Timing Patterns</Label>
                <p className="text-sm text-slate-500">Learn optimal response and follow-up times</p>
              </div>
            </div>
            <Switch
              checked={config?.settings?.include_timing_patterns}
              onCheckedChange={(checked) => updateSetting('include_timing_patterns', checked)}
              data-testid="timing-patterns"
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <MessageSquare className="h-5 w-5 text-green-600" />
              <div>
                <Label className="font-medium">Channel Patterns</Label>
                <p className="text-sm text-slate-500">Learn preferred communication channels</p>
              </div>
            </div>
            <Switch
              checked={config?.settings?.include_channel_patterns}
              onCheckedChange={(checked) => updateSetting('include_channel_patterns', checked)}
              data-testid="channel-patterns"
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Phone className="h-5 w-5 text-green-600" />
              <div>
                <Label className="font-medium">Action Sequence Patterns</Label>
                <p className="text-sm text-slate-500">Learn successful action sequences</p>
              </div>
            </div>
            <Switch
              checked={config?.settings?.include_sequence_patterns}
              onCheckedChange={(checked) => updateSetting('include_sequence_patterns', checked)}
              data-testid="sequence-patterns"
            />
          </div>
        </CardContent>
      </Card>

      {/* Sample Playbook Preview */}
      <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-green-600" />
            Sample Playbook Preview
          </CardTitle>
          <CardDescription>
            Example of what a generated playbook looks like
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[
              { step: 1, action: 'Call within 15 minutes', icon: Phone, timing: 'Immediately' },
              { step: 2, action: 'Send WhatsApp with details', icon: MessageSquare, timing: '1 hour if no answer' },
              { step: 3, action: 'Follow-up call', icon: Phone, timing: 'Next day' },
              { step: 4, action: 'Schedule meeting', icon: Calendar, timing: 'When interested' },
            ].map(({ step, action, icon: Icon, timing }) => (
              <div key={step} className="flex items-center gap-4 p-3 bg-white rounded-lg shadow-sm">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-medium text-sm">
                  {step}
                </div>
                <Icon className="h-4 w-4 text-green-600" />
                <div className="flex-1">
                  <p className="font-medium text-sm">{action}</p>
                  <p className="text-xs text-slate-500">{timing}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4 border-t">
        <Button
          variant="outline"
          onClick={handleReset}
          className="text-slate-600"
          data-testid="reset-btn"
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          Reset to Defaults
        </Button>
        
        <Button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className="bg-green-600 hover:bg-green-700"
          data-testid="save-btn"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Configuration
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default AIAgentPlaybook;
